// THE adapter between the CRM tab's filter shape and the listings page's search state.
//
// The "הוספת נכסים" sub-tab keeps its own lean `MatchFilters` (see contactMatchFilters.ts),
// while the full filters panel we reuse — listingsMatch/ListingsFiltersDrawer — is written
// against the listings page's much wider `SearchState`. Rather than fork a second 400-line
// panel, the drawer is driven through its `contextOverride` prop and the two shapes are
// translated HERE, in one file, in both directions. Nothing else in the matches folder may
// convert between them; if a conversion is needed somewhere else, export it from here.
//
// ── field-by-field mapping ────────────────────────────────────────────────────────────
//   SearchState                     MatchFilters                  applied by
//   listingListedFor            ⇄   listedFor                     buildMatchSearchRequest
//   locations                   ⇄   locations                     searchMatchListings (sub-queries)
//   minPrice / maxPrice         ⇄   minPrice / maxPrice           buildMatchSearchRequest
//   includeListingsWithoutPr…   ⇄   includeListingsWithoutPr…     buildMatchSearchRequest
//   minRooms / maxRooms         ⇄   minRooms / maxRooms           buildMatchSearchRequest
//   minFloors / maxFloors       ⇄   minFloors / maxFloors         buildMatchSearchRequest
//   minSquareMeter / max…       ⇄   minSquareMeter / max…         buildMatchSearchRequest
//   propertyTypes               ⇄   propertyTypes                 buildMatchSearchRequest
//   booleanPropertiesDetails    ⇄   booleanProps                  buildMatchSearchRequest
//   minParkingSpots/max…        ⇄   minParkingSpots/max…          buildMatchSearchRequest
//   adTypes                     ⇄   adTypes                       buildMatchSearchRequest
//   agencyNames / sellerNames   ⇄   agencyNames / sellerNames     buildMatchSearchRequest
//   isAssetExclusive            ⇄   isAssetExclusive              buildMatchSearchRequest
//   onlyPriceChanged            ⇄   onlyPriceChanged              buildMatchSearchRequest
//   publishedDateRange          ⇄   publishedDateRange            buildMatchSearchRequest
//   hidePotentiallyRemoved…     ⇄   hidePotentiallyRemoved…       searchMatchListings (per-origin)
//   searchTerm                      — (not rendered by the drawer; the sub-tab's own
//                                      free-text box owns it, outside MatchFilters)
//   listingOrigins                  — FROZEN, see CRM_FROZEN_FILTERS
//   pickedContact                   — listings-page-only concept
import type { SearchState } from "contexts/ListingsMatchContext";
import { ListingOrigins } from "../../../../../../@types/sharedSchema";
import type { Room, Floor, ParkingSpot } from "../../../../../../@types/sharedSchema";
import type { FrozenFilters } from "components/_dashboard/listingsMatch/ListingsFiltersDrawer";
import type { MatchFilters } from "./contactMatchFilters";

/**
 * Controls the drawer must NOT render on this surface.
 *
 * `listingOrigins` is the only one: the discovery search always queries all three
 * origins and splits the hits into the הנכסים שלי / נכסי המשרד / מהשוק tab strip.
 * An origin checkbox here would empty a tab that still advertises its own count —
 * the strip IS the origin control on this surface.
 *
 * Everything else the drawer offers is genuinely applied by the CRM search request
 * (see buildMatchSearchRequest), so it stays visible and working.
 */
export const CRM_FROZEN_FILTERS: FrozenFilters = { listingOrigins: true };

/** MatchFilters → the SearchState the drawer edits (its draft seed). */
export function matchFiltersToSearchState(filters: MatchFilters): SearchState {
  return {
    // Inert on this surface but part of the shape the drawer's context type demands.
    searchTerm: "",
    pickedContact: undefined,
    // Frozen out of the UI; kept complete so nothing the drawer reads is undefined.
    listingOrigins: [...ListingOrigins],

    listingListedFor: filters.listedFor,
    locations: filters.locations ?? [],
    minPrice: filters.minPrice,
    maxPrice: filters.maxPrice,
    includeListingsWithoutPrice: filters.includeListingsWithoutPrice ?? false,
    minRooms: filters.minRooms,
    maxRooms: filters.maxRooms,
    minFloors: filters.minFloors,
    maxFloors: filters.maxFloors,
    minSquareMeter: filters.minSquareMeter,
    maxSquareMeter: filters.maxSquareMeter,
    propertyTypes: filters.propertyTypes ?? [],
    booleanPropertiesDetails: { ...(filters.booleanProps ?? {}) },

    minParkingSpots: filters.minParkingSpots,
    maxParkingSpots: filters.maxParkingSpots,
    adTypes: filters.adTypes ?? [],
    agencyNames: filters.agencyNames ?? [],
    sellerNames: filters.sellerNames ?? [],
    isAssetExclusive: filters.isAssetExclusive ?? false,
    onlyPriceChanged: filters.onlyPriceChanged ?? false,
    publishedDateRange: filters.publishedDateRange,
    // undefined means ON for MatchFilters; the drawer's radio needs a real boolean.
    hidePotentiallyRemovedListings: filters.hidePotentiallyRemovedListings !== false
  };
}

/**
 * The drawer's committed draft → a MatchFilters patch for the tab's `onFiltersChange`.
 *
 * `current` is the filter state the draft was seeded from, and is used for ONE rule:
 * when the realtor flips סוג עסקה (sale ⇄ rent) inside the drawer without touching the
 * price fields, the price bounds are omitted from the patch so MatchesTab re-seeds them
 * from the contact's budget for the NEW transaction type. Carrying a sale-scale budget
 * into a rent search guarantees zero hits. This mirrors exactly what the chip row's
 * rent/sale toggle already does — the rule lives in MatchesTab.handleFiltersChange, and
 * omitting the keys is how this path opts into it.
 */
export function drawerDraftToFiltersPatch(
  draft: SearchState,
  current: MatchFilters
): Partial<MatchFilters> {
  const patch: Partial<MatchFilters> = {
    listedFor: draft.listingListedFor,
    locations: draft.locations ?? [],
    minRooms: draft.minRooms as Room,
    maxRooms: draft.maxRooms as Room,
    minFloors: draft.minFloors as Floor,
    maxFloors: draft.maxFloors as Floor,
    minSquareMeter: draft.minSquareMeter,
    maxSquareMeter: draft.maxSquareMeter,
    propertyTypes: draft.propertyTypes ?? [],
    booleanProps: { ...(draft.booleanPropertiesDetails ?? {}) },

    minParkingSpots: draft.minParkingSpots as ParkingSpot,
    maxParkingSpots: draft.maxParkingSpots as ParkingSpot,
    adTypes: draft.adTypes ?? [],
    agencyNames: draft.agencyNames ?? [],
    sellerNames: draft.sellerNames ?? [],
    isAssetExclusive: draft.isAssetExclusive ?? false,
    onlyPriceChanged: draft.onlyPriceChanged ?? false,
    publishedDateRange: draft.publishedDateRange,
    hidePotentiallyRemovedListings: draft.hidePotentiallyRemovedListings !== false
  };

  const flippedListedFor = draft.listingListedFor !== current.listedFor;
  const priceUntouched =
    draft.minPrice === current.minPrice && draft.maxPrice === current.maxPrice;
  if (!(flippedListedFor && priceUntouched)) {
    patch.minPrice = draft.minPrice;
    patch.maxPrice = draft.maxPrice;
  }
  // Not part of the budget re-seed above: a sale⇄rent flip re-derives the BOUNDS from
  // the contact's budget for the new type, but "also show unpriced listings" is a
  // preference about the result list, and the realtor set it deliberately.
  patch.includeListingsWithoutPrice = draft.includeListingsWithoutPrice ?? false;

  return patch;
}

// ── advanced-filter summary (for the chip row) ────────────────────────────────────────
// The chip row stays the at-a-glance summary of what is active, so every advanced filter
// the drawer can set has to be visible — and individually clearable — from the row.

export type AdvancedFilterSummary = {
  /** Stable key for React lists. */
  id: string;
  /** Short Hebrew label, matching the drawer's own wording. */
  label: string;
  /** Patch that turns this one filter off. */
  clearPatch: Partial<MatchFilters>;
};

const AD_TYPE_LABELS: Record<string, string> = { commercial: "תיווך", private: "פרטי" };

const DATE_PRESET_LABELS: Record<string, string> = {
  last30: "30 הימים האחרונים",
  last90: "90 הימים האחרונים",
  lastYear: "השנה האחרונה",
  custom: "טווח מותאם"
};

const listLabel = (title: string, values: string[], map?: Record<string, string>): string => {
  const shown = map ? values.map((v) => map[v] ?? v) : values;
  return shown.length === 1 ? `${title}: ${shown[0]}` : `${title}: ${shown[0]} +${shown.length - 1}`;
};

/**
 * One entry per ACTIVE advanced filter. Drives both the summary chips and the count
 * badge on the "מסננים מתקדמים" button, so the two can never disagree.
 */
export function advancedFilterSummaries(filters: MatchFilters): AdvancedFilterSummary[] {
  const summaries: AdvancedFilterSummary[] = [];

  const { minParkingSpots: minParking, maxParkingSpots: maxParking } = filters;
  if (minParking !== undefined || maxParking !== undefined) {
    const range =
      minParking !== undefined && maxParking !== undefined
        ? `${minParking}–${maxParking}`
        : minParking !== undefined
        ? `${minParking}+`
        : `עד ${maxParking}`;
    summaries.push({
      id: "parking",
      label: `חניות: ${range}`,
      clearPatch: { minParkingSpots: undefined, maxParkingSpots: undefined }
    });
  }

  if (filters.adTypes && filters.adTypes.length > 0) {
    summaries.push({
      id: "adTypes",
      label: listLabel("תיווך/פרטי", filters.adTypes, AD_TYPE_LABELS),
      clearPatch: { adTypes: [] }
    });
  }

  if (filters.agencyNames && filters.agencyNames.length > 0) {
    summaries.push({
      id: "agencyNames",
      label: listLabel("משרד", filters.agencyNames),
      clearPatch: { agencyNames: [] }
    });
  }

  if (filters.sellerNames && filters.sellerNames.length > 0) {
    summaries.push({
      id: "sellerNames",
      label: listLabel("מתווך", filters.sellerNames),
      clearPatch: { sellerNames: [] }
    });
  }

  if (filters.isAssetExclusive) {
    summaries.push({
      id: "isAssetExclusive",
      label: "בבלעדיות בלבד",
      clearPatch: { isAssetExclusive: false }
    });
  }

  if (filters.onlyPriceChanged) {
    summaries.push({
      id: "onlyPriceChanged",
      label: "עם שינוי מחיר בלבד",
      clearPatch: { onlyPriceChanged: false }
    });
  }

  const range = filters.publishedDateRange;
  if (range && range.preset !== "all") {
    summaries.push({
      id: "publishedDateRange",
      label: `פורסם: ${DATE_PRESET_LABELS[range.preset] ?? range.preset}`,
      clearPatch: { publishedDateRange: undefined }
    });
  }

  // Only the NON-default value is worth a chip: age-hiding is on by default, so the
  // chip announces the unusual state ("stale listings are showing"), not the normal one.
  if (filters.hidePotentiallyRemovedListings === false) {
    summaries.push({
      id: "hidePotentiallyRemovedListings",
      label: "כולל מודעות שייתכן שהוסרו",
      clearPatch: { hidePotentiallyRemovedListings: true }
    });
  }

  return summaries;
}
